import React, { useRef, useState } from "react";
import { ViewportPortal } from "../shared/ViewportDialog";
import { CP_MODAL_OVERLAY_STYLE, CP_MODAL_PANEL_STYLE } from "../shared/crewpilotModalStyles";
import { downloadCsv } from "../../helpers/accountantCsvExport";
import { isOffline } from "../../helpers/connectivity";
import {
  IMPORT_COLUMNS,
  IMPORT_MAX_BYTES,
  ROW_STATUS,
  buildMaterialsImportTemplate,
  commitMaterialsImport,
  loadExistingMaterialsForImport,
  recheckDuplicatesBeforeSubmit,
  prepareMaterialsImport,
} from "../../helpers/materialsCsvImport";
import "./MaterialsImportDialog.css";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

function RowList({ title, rows, tone }) {
  if (!rows.length) return null;
  return (
    <details className={`mimp__group mimp__group--${tone}`} open={rows.length <= 10}>
      <summary>{title} ({rows.length})</summary>
      <ul>
        {rows.slice(0, 200).map((r) => (
          <li key={`${r.line}-${r.id || "x"}`}>
            <b>Row {r.line}</b> {r.form.material_name || <i>(no name)</i>}
            {r.reasons?.length ? <span> — {r.reasons.join(" ")}</span> : null}
          </li>
        ))}
        {rows.length > 200 ? <li>…and {rows.length - 200} more</li> : null}
      </ul>
    </details>
  );
}

/**
 * B5: Materials CSV import. Select file → preview → confirm → truthful result.
 * Nothing is written before Confirm; Cancel at any earlier step saves nothing.
 */
export function MaterialsImportDialog({ actor, onClose, onImported }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState("select"); // select | preview | importing | result
  const [error, setError] = useState("");
  const [prepared, setPrepared] = useState(null);
  const [includeDuplicates, setIncludeDuplicates] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState("");
  const [lateDuplicates, setLateDuplicates] = useState([]);

  async function handleFile(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError("");
    setFileName(file.name);
    if (!/\.csv$/i.test(file.name) && !/csv|text\/plain/i.test(file.type || "")) {
      setError("Choose a .csv file.");
      return;
    }
    if (file.size > IMPORT_MAX_BYTES) {
      setError("This file is larger than 2 MB. Split it into smaller files.");
      return;
    }
    if (isOffline()) {
      setError("You're offline. Importing materials needs a connection.");
      return;
    }
    try {
      const text = await file.text();
      const existing = await loadExistingMaterialsForImport(actor);
      const prep = prepareMaterialsImport(text, { existing });
      if (prep.error) {
        setError(prep.error);
        return;
      }
      setPrepared(prep);
      setIncludeDuplicates(false);
      setStep("preview");
    } catch (e) {
      setError(e?.message || "The file could not be read.");
    }
  }

  const toImport = prepared
    ? prepared.rows.filter((r) => r.status === ROW_STATUS.VALID || (includeDuplicates && r.status === ROW_STATUS.DUPLICATE))
    : [];

  async function runImport(entries, { recheck = false } = {}) {
    if (isOffline()) {
      setError("You're offline. Importing materials needs a connection; nothing was saved.");
      return;
    }
    setStep("importing");
    setError("");
    setProgress({ done: 0, total: entries.length });
    try {
      let toSend = entries;
      if (recheck) {
        // Re-read the catalog right before the first submit (see helper for limits).
        const fresh = await loadExistingMaterialsForImport(actor);
        const checked = recheckDuplicatesBeforeSubmit(entries, fresh);
        toSend = checked.send;
        setLateDuplicates(checked.skipped);
      }
      const res = await commitMaterialsImport(toSend, { actor, onProgress: setProgress });
      setResult((prev) => {
        if (!prev) return res;
        // Retry of the same prepared rows (same ids): merge with the earlier attempt.
        return {
          imported: [...prev.imported, ...res.imported],
          failed: res.failed,
          notAttempted: res.notAttempted,
          stoppedReason: res.stoppedReason,
        };
      });
      if (res.imported.length) onImported?.();
      setStep("result");
    } catch (e) {
      setError(e?.message || "Import failed. Nothing was saved.");
      setStep(result ? "result" : "preview");
    }
  }

  const skipped = prepared
    ? prepared.rows.filter((r) => r.status === ROW_STATUS.INVALID || (!includeDuplicates && r.status === ROW_STATUS.DUPLICATE))
    : []
  const skippedAll = [...skipped, ...lateDuplicates];
  const retryable = result ? [...result.failed.map((f) => f.entry), ...result.notAttempted] : [];
  const busy = step === "importing";

  return (
    <ViewportPortal>
      <div style={CP_MODAL_OVERLAY_STYLE} role="presentation">
        <div style={{ ...CP_MODAL_PANEL_STYLE, maxWidth: "640px" }} className="mimp" role="dialog" aria-modal="true" aria-labelledby="mimp-title">
          <h3 id="mimp-title" style={{ marginTop: 0 }}>Import materials from CSV</h3>

          {step === "select" ? (
            <>
              <p className="mimp__note">
                Columns: {IMPORT_COLUMNS.map((c) => (c.required ? `${c.header} (required)` : c.header)).join(", ")}.
                Type is Material or Service (blank = Material). Unit Cost and Inventory are plain numbers (no $ or commas).
                Existing materials are never changed.
              </p>
              <div className="mimp__actions">
                <button type="button" className="nx-btn" onClick={() => downloadCsv("CrewPilot_Materials_Import_Template.csv", buildMaterialsImportTemplate())}>
                  Download template
                </button>
                <button type="button" className="nx-btn" onClick={() => fileRef.current?.click()} data-testid="mimp-choose">
                  Choose CSV file
                </button>
                <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} hidden data-testid="mimp-file" aria-label="CSV file" />
              </div>
            </>
          ) : null}

          {step === "preview" && prepared ? (
            <>
              <p className="mimp__note">{fileName}</p>
              <div className="mimp__counts" role="status">
                <span className="mimp__chip mimp__chip--ok">{plural(prepared.counts.valid, "valid row")}</span>
                <span className="mimp__chip mimp__chip--warn">{plural(prepared.counts.duplicate, "possible duplicate")}</span>
                <span className="mimp__chip mimp__chip--bad">{plural(prepared.counts.invalid, "invalid row")}</span>
              </div>
              {prepared.unknownColumns?.length ? (
                <p className="mimp__note">Ignored columns: {prepared.unknownColumns.join(", ")}</p>
              ) : null}
              <RowList title="Invalid (will be skipped)" rows={prepared.rows.filter((r) => r.status === ROW_STATUS.INVALID)} tone="bad" />
              <RowList title="Possible duplicates (skipped unless you choose otherwise)" rows={prepared.rows.filter((r) => r.status === ROW_STATUS.DUPLICATE)} tone="warn" />
              {prepared.counts.duplicate ? (
                <label className="mimp__check">
                  <input type="checkbox" checked={includeDuplicates} onChange={(e) => setIncludeDuplicates(e.target.checked)} />
                  <span>Also import possible duplicates as new materials (existing materials stay unchanged)</span>
                </label>
              ) : null}
              <div className="mimp__actions">
                <button type="button" className="nx-btn" onClick={onClose}>Cancel</button>
                <button type="button" className="nx-btn mimp__primary" disabled={!toImport.length} onClick={() => runImport(toImport, { recheck: true })} data-testid="mimp-confirm">
                  Import {plural(toImport.length, "material")}
                </button>
              </div>
            </>
          ) : null}

          {step === "importing" ? (
            <p className="mimp__note" role="status">
              Importing… {progress ? `${progress.done} of ${progress.total}` : ""} Keep this screen open.
            </p>
          ) : null}

          {step === "result" && result ? (
            <>
              <div className="mimp__counts" role="status" data-testid="mimp-result">
                <span className="mimp__chip mimp__chip--ok">{result.imported.length} imported</span>
                <span className="mimp__chip mimp__chip--warn">{skippedAll.length} skipped</span>
                <span className="mimp__chip mimp__chip--bad">{result.failed.length + result.notAttempted.length} failed / not sent</span>
              </div>
              {result.stoppedReason ? <p className="mimp__msg mimp__msg--bad" role="alert">{result.stoppedReason}</p> : null}
              <RowList title="Failed" rows={result.failed.map((f) => ({ ...f.entry, reasons: [f.reason] }))} tone="bad" />
              <RowList title="Not sent" rows={result.notAttempted} tone="bad" />
              <RowList title="Skipped" rows={skippedAll} tone="warn" />
              <div className="mimp__actions">
                {retryable.length ? (
                  <button type="button" className="nx-btn" onClick={() => runImport(retryable)} data-testid="mimp-retry">
                    Retry {plural(retryable.length, "row")}
                  </button>
                ) : null}
                <button type="button" className="nx-btn mimp__primary" onClick={onClose}>Done</button>
              </div>
            </>
          ) : null}

          {error ? <p className="mimp__msg mimp__msg--bad" role="alert">{error}</p> : null}
          {step === "select" ? (
            <div className="mimp__actions"><button type="button" className="nx-btn" onClick={onClose} disabled={busy}>Cancel</button></div>
          ) : null}
        </div>
      </div>
    </ViewportPortal>
  );
}
